import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthProvider';
import SlackNavigation from './SlackNavigation';
import Dashboard from './Dashboard';
import WorkStreamTab from './WorkStreamTab';
import './MultiCampaignDashboard.css';
import './Dashboard.css';

// Helper function to create default campaigns
const createDefaultCampaigns = () => {
  const timestamp = Date.now();
  const salesCampaignId = `campaign_${timestamp}_sales`;
  const marketingCampaignId = `campaign_${timestamp}_marketing`;
  const engineeringCampaignId = `campaign_${timestamp}_engineering`;

  const salesCampaign = {
    id: salesCampaignId,
    name: 'Sales 1',
    function: 'sales',
    state: {
      isInitializing: false,
      isStarting: false,
      error: null,
      metrics: {},
      startTime: new Date().toISOString(),
      campaign_id: salesCampaignId,
      status: 'stopped',
      url: '',
      agents: [],
      campaignActive: false,
    },
    createdAt: new Date().toISOString(),
  };
  
  const marketingCampaign = {
    id: marketingCampaignId,
    name: 'Marketing 1',
    function: 'marketing',
    state: {
      isInitializing: false,
      isStarting: false,
      error: null,
      metrics: {},
      startTime: new Date().toISOString(),
      campaign_id: marketingCampaignId,
      status: 'stopped',
      url: '',
      agents: [],
      campaignActive: false,
    },
    createdAt: new Date().toISOString(),
  };

  const engineeringCampaign = {
    id: engineeringCampaignId,
    name: 'Engineering 1',
    function: 'engineering',
    state: {
      isInitializing: false,
      isStarting: false,
      error: null,
      metrics: {},
      startTime: new Date().toISOString(),
      campaign_id: engineeringCampaignId,
      status: 'stopped',
      url: '',
      agents: [],
      campaignActive: false,
    },
    createdAt: new Date().toISOString(),
  };


  return {
    campaigns: { 
      [salesCampaignId]: salesCampaign,
      [marketingCampaignId]: marketingCampaign,
      [engineeringCampaignId]: engineeringCampaign

    },
    salesCampaignId
  };
};

// Initialize with default campaigns if none exist in localStorage
const getInitialCampaigns = () => {
  const savedCampaigns = localStorage.getItem('grupa_campaigns');
  if (savedCampaigns) {
    try {
      const parsed = JSON.parse(savedCampaigns);
      if (typeof parsed === 'object' && parsed !== null) {
        // Reset isInitializing to false for all campaigns when loading from storage
        // This prevents InitializingUI from showing after page reload when campaign is not running
        const campaignsWithResetState = {};
        for (const [id, campaign] of Object.entries(parsed)) {
          campaignsWithResetState[id] = {
            ...campaign,
            state: {
              ...campaign.state,
              isInitializing: false,  // Always reset on page load
              isStarting: false,      // Also reset starting state
              status: campaign.state.status === 'resuming' ? 'stopped' : campaign.state.status,  // Reset resuming to stopped
              // Keep other state like metrics, etc.
            }
          };
        }
        return campaignsWithResetState;
      }
      return createDefaultCampaigns().campaigns;
    } catch (error) {
      console.error('Error parsing campaigns from localStorage:', error);
      return createDefaultCampaigns().campaigns;
    }
  } else {
    const { campaigns } = createDefaultCampaigns();
    localStorage.setItem('grupa_campaigns', JSON.stringify(campaigns));
    return campaigns;
  }
};

const getInitialActiveCampaignId = (campaigns) => {
  const salesCampaigns = Object.values(campaigns).filter(c => c.function === 'sales');
  return salesCampaigns.length > 0 ? salesCampaigns[0].id : Object.keys(campaigns)[0] || null;
};

export function MultiCampaignDashboard() {
  const initialCampaigns = getInitialCampaigns();
  const [campaigns, setCampaigns] = useState(initialCampaigns);
  const [activeCampaignId, setActiveCampaignId] = useState(getInitialActiveCampaignId(initialCampaigns));
  const [activeFunction, setActiveFunction] = useState('sales');
  const [viewMode, setViewMode] = useState('workstream');
  const { user, logout } = useAuth();

  // Save campaigns to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('grupa_campaigns', JSON.stringify(campaigns));
  }, [campaigns]);

  const createCampaign = useCallback((functionType) => {
    const newCampaignId = `campaign_${Date.now()}`;
    const functionCampaigns = Object.values(campaigns).filter(c => c.function === functionType);
    const newCampaign = {
      id: newCampaignId,
      name: `${functionType.charAt(0).toUpperCase() + functionType.slice(1)} ${functionCampaigns.length + 1}`,
      function: functionType,
      state: {
        isInitializing: false,
        isStarting: false,
        error: null,
        metrics: {},
        startTime: new Date().toISOString(),
        campaign_id: newCampaignId,
        status: 'stopped',
        url: '',
        agents: [],
        campaignActive: false,
      },
      createdAt: new Date().toISOString(),
    };

    setCampaigns(prev => ({
      ...prev,
      [newCampaignId]: newCampaign
    }));
    setActiveCampaignId(newCampaignId);
  }, [campaigns]);

  const closeCampaign = useCallback((campaignId) => {
    setCampaigns(prev => {
      const updated = { ...prev };
      delete updated[campaignId];
      return updated;
    });

    // Switch to another campaign if this was active
    if (activeCampaignId === campaignId) {
      const remainingCampaigns = Object.keys(campaigns).filter(id => id !== campaignId);
      if (remainingCampaigns.length > 0) {
        setActiveCampaignId(remainingCampaigns[0]);
        // If we were in campaign view, stay in campaign view but switch ID
        // If we were in workstream view, stay in workstream view
      } else {
        setActiveCampaignId(null);
        setViewMode('workstream'); // Fallback to workstream if no campaigns
      }
    }
  }, [activeCampaignId, campaigns]);

  const updateCampaignState = useCallback((campaignId, newState) => {
    setCampaigns(prev => ({
      ...prev,
      [campaignId]: {
        ...prev[campaignId],
        state: { ...prev[campaignId].state, ...newState }
      }
    }));
  }, []);

  const switchCampaign = useCallback((campaignId) => {
    setActiveCampaignId(campaignId);
    setViewMode('campaign');
  }, []);

  const activeCampaign = activeCampaignId ? campaigns[activeCampaignId] : null;

  return (
    <div className="multi-campaign-dashboard">
      <SlackNavigation
        campaigns={campaigns}
        activeCampaignId={activeCampaignId}
        activeFunction={activeFunction}
        viewMode={viewMode}
        onCreateCampaign={createCampaign}
        onCloseCampaign={closeCampaign}
        onSwitchCampaign={switchCampaign}
        onSetActiveFunction={setActiveFunction}
        onSetViewMode={setViewMode}
        user={user}
        onLogout={logout}
      />
      <div className="dashboard-content">
        {viewMode === 'workstream' ? (
          <WorkStreamTab />
        ) : (
          <Dashboard
            campaignId={activeCampaign?.id || 'default'}
            campaignName={activeCampaign?.name || 'Campaign'}
            campaignState={activeCampaign?.state || {
              isInitializing: false,
              isStarting: false,
              error: null,
              metrics: {},
              startTime: new Date().toISOString(),
              campaign_id: 'default',
              status: 'stopped',
            }}
            onCampaignStateChange={activeCampaign ? (newState) => updateCampaignState(activeCampaign.id, newState) : () => {}}
            user={user}
            onLogout={logout}
          />
        )}
      </div>
    </div>
  );
}